import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { X, MapPin, Clock, Euro, ArrowRight, Globe, Phone, Instagram } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SourceBadge } from '@/components/cards/primitives'
import { useI18n } from '@/contexts/I18nContext'
import type { PreviewData } from '@/hooks/usePreviewSheet'

const HERO_GRADIENTS: ReadonlyArray<{ gradient: string; accent: string }> = [
  { gradient: 'from-violet-950 via-purple-900 to-indigo-950', accent: 'text-violet-300' },
  { gradient: 'from-amber-950 via-orange-900 to-red-950',    accent: 'text-amber-300'  },
  { gradient: 'from-teal-950 via-emerald-900 to-cyan-950',   accent: 'text-teal-300'   },
  { gradient: 'from-slate-900 via-zinc-900 to-stone-950',    accent: 'text-zinc-300'   },
  { gradient: 'from-rose-950 via-pink-900 to-fuchsia-950',   accent: 'text-rose-300'   },
  { gradient: 'from-blue-950 via-indigo-900 to-violet-950',  accent: 'text-blue-300'   },
]

function nameHash(name: string): number {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0
  return Math.abs(h)
}

function getInitials(name: string): string {
  const cleaned = name.replace(/[^a-zA-Z\s]/g, '')
  const words = cleaned.split(/\s+/).filter(Boolean)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  if (words.length === 1) return words[0][0].toUpperCase()
  return name[0].toUpperCase()
}

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          style={{ fontSize: '0.875rem', lineHeight: 1 }}
          className={
            rating >= i - 0.25
              ? 'text-amber-400'
              : rating >= i - 0.75
              ? 'text-amber-400/50'
              : 'text-white/20'
          }
        >
          &#9733;
        </span>
      ))}
    </div>
  )
}

function useCountUp(target: number | null | undefined, durationMs: number, disabled: boolean): number {
  const [value, setValue] = useState(0)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    if (target == null) return
    if (disabled) { setValue(target); return }

    setValue(0)
    const startTime = performance.now()
    const run = (now: number) => {
      const p = Math.min((now - startTime) / durationMs, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      setValue(target * eased)
      if (p < 1) rafRef.current = requestAnimationFrame(run)
    }
    rafRef.current = requestAnimationFrame(run)
    return () => cancelAnimationFrame(rafRef.current)
  }, [target, durationMs, disabled])

  return value
}

type ChipType = 'city' | 'open' | 'closed' | 'ggd' | 'source' | 'style' | 'price'

interface Chip {
  key: string
  label: string
  type: ChipType
}

function chipColorClass(type: ChipType): string {
  switch (type) {
    case 'open':   return 'bg-emerald-500/15 text-emerald-400'
    case 'closed': return 'bg-red-500/15 text-red-400'
    case 'ggd':    return 'bg-sky-500/15 text-sky-400'
    case 'price':  return 'bg-amber-500/15 text-amber-400'
    case 'style':  return 'bg-violet-500/15 text-violet-400'
    default:       return 'bg-muted text-muted-foreground'
  }
}

function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : false
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isDesktop
}

function buildProfileUrl(data: PreviewData): string {
  if (data.profileType === 'claimed') return `/c/${data.slug}`
  if (data.countrySlug && data.citySlug) return `/tattoo/${data.countrySlug}/${data.citySlug}/${data.slug}`
  return `/studio/${data.slug}`
}

interface StudioPreviewSheetProps {
  data: PreviewData
  onClose: () => void
}

export default function StudioPreviewSheet({ data, onClose }: StudioPreviewSheetProps) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const prefersReduced = useReducedMotion()
  const isDesktop = useIsDesktop()
  const firstFocusRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  useEffect(() => { firstFocusRef.current?.focus() }, [])

  const [imgFailed, setImgFailed] = useState(false)
  const profileUrl = buildProfileUrl(data)
  const hash = nameHash(data.name)
  const { gradient, accent } = HERO_GRADIENTS[hash % HERO_GRADIENTS.length]
  const initials = getInitials(data.name)
  const animatedRating = useCountUp(data.rating, 600, !!prefersReduced)

  const circles = [
    { left: (hash % 60) + 5,           top: ((hash >> 3) % 50) + 5,    size: 80 + (hash % 60)         },
    { left: ((hash >> 6) % 50) + 30,   top: ((hash >> 9) % 60) + 20,   size: 50 + ((hash >> 6) % 40)  },
    { left: ((hash >> 12) % 40) + 45,  top: ((hash >> 15) % 40) + 40,  size: 100 + ((hash >> 12) % 80) },
  ]

  const sourceType = data.sourceType ?? null
  const validSource =
    sourceType && (['google', 'osm', 'rivm', 'kbo'] as const).includes(sourceType as 'google' | 'osm' | 'rivm' | 'kbo')
      ? (sourceType as 'google' | 'osm' | 'rivm' | 'kbo')
      : null

  const chips: Chip[] = []
  if (data.city) chips.push({ key: 'city', label: data.city, type: 'city' })
  if (data.isOpenNow === true) chips.push({ key: 'open', label: t('preview.open') || 'Nu open', type: 'open' })
  else if (data.isOpenNow === false) chips.push({ key: 'closed', label: t('preview.closed') || 'Gesloten', type: 'closed' })
  if (data.hasGgdPermit) chips.push({ key: 'ggd', label: 'GGD permit', type: 'ggd' })
  if (data.priceFrom != null) chips.push({ key: 'price', label: `v.a. €${data.priceFrom}`, type: 'price' })
  data.styles?.slice(0, 3).forEach((s) => chips.push({ key: s, label: s.replace(/_/g, ' '), type: 'style' }))

  const sheetMotionProps = prefersReduced
    ? {}
    : isDesktop
    ? {
        initial: { x: '100%' },
        animate: { x: 0, transition: { type: 'spring' as const, damping: 32, stiffness: 300 } },
        exit: { x: '100%', transition: { duration: 0.22, ease: [0.4, 0, 1, 1] as [number, number, number, number] } },
      }
    : {
        initial: { y: '100%' },
        animate: { y: 0, transition: { type: 'spring' as const, damping: 32, stiffness: 300 } },
        exit: { y: '100%', transition: { duration: 0.22, ease: [0.4, 0, 1, 1] as [number, number, number, number] } },
        drag: 'y' as const,
        dragConstraints: { top: 0 },
        dragElastic: { top: 0, bottom: 0.4 },
        onDragEnd: (_: unknown, info: { offset: { y: number } }) => {
          if (info.offset.y > 100) onClose()
        },
      }

  const backdropMotionProps = prefersReduced
    ? {}
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.2 },
      }

  const stagger = (i: number) =>
    prefersReduced
      ? {}
      : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0, transition: { delay: 0.1 + i * 0.05, duration: 0.25, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } } }

  return (
    <>
      <motion.div
        className="fixed inset-0 z-[1050] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        {...backdropMotionProps}
      />

      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={data.name}
        className={
          isDesktop
            ? 'fixed inset-y-0 right-0 z-[1100] w-[420px] bg-background border-l border-border overflow-y-auto shadow-2xl flex flex-col'
            : 'fixed inset-x-0 bottom-0 z-[1100] bg-background rounded-t-2xl overflow-y-auto shadow-2xl flex flex-col max-h-[82vh]'
        }
        {...sheetMotionProps}
      >
        {/* ── HERO ── */}
        <div className={`relative shrink-0 overflow-hidden ${isDesktop ? 'h-52' : 'h-48'}`}>
          {!isDesktop && (
            <div className="absolute top-2.5 inset-x-0 flex justify-center z-20 pointer-events-none">
              <div className="w-10 h-1 rounded-full bg-white/30" />
            </div>
          )}

          {data.photoUrl && !imgFailed ? (
            <>
              <img
                src={data.photoUrl}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                onError={() => setImgFailed(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            </>
          ) : (
            <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`}>
              {circles.map((c, i) => (
                <div
                  key={i}
                  className="absolute rounded-full bg-white pointer-events-none"
                  style={{
                    left: `${c.left}%`,
                    top: `${c.top}%`,
                    width: c.size,
                    height: c.size,
                    opacity: 0.04 + i * 0.015,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              ))}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                  <span className={`text-3xl font-black select-none ${accent}`}>{initials}</span>
                </div>
              </div>
            </div>
          )}

          <button
            ref={firstFocusRef}
            onClick={onClose}
            aria-label={t('preview.close') || 'Sluiten'}
            className="absolute top-3 right-3 z-10 rounded-full p-1.5 bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          {data.rating != null && (
            <div className="absolute bottom-3 left-4 flex items-end gap-3">
              <div>
                <div
                  className="text-4xl font-black text-white leading-none tabular-nums drop-shadow-md"
                  aria-label={`Beoordeling: ${data.rating.toFixed(1)}`}
                >
                  {animatedRating.toFixed(1)}
                </div>
                <StarRow rating={data.rating} />
              </div>
              {data.reviewCount != null && (
                <p className="text-xs text-white/70 leading-tight mb-0.5">
                  {data.reviewCount}
                  <br />
                  reviews
                </p>
              )}
            </div>
          )}

          {data.styles && data.styles.length > 0 && (
            <div className="absolute bottom-3 right-4 flex flex-col gap-1 items-end">
              {data.styles.slice(0, 2).map((s) => (
                <span
                  key={s}
                  className="bg-black/50 backdrop-blur-sm text-white text-[10px] px-2 py-0.5 rounded-full leading-snug"
                >
                  {s.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── CONTENT ── */}
        <div className="flex flex-col flex-1">
          <motion.div className="px-4 pt-4 pb-1 shrink-0" {...stagger(0)}>
            <h2 className="text-xl font-bold text-foreground leading-tight line-clamp-2">{data.name}</h2>
            {validSource && (
              <div className="mt-1.5">
                <SourceBadge src={validSource} />
              </div>
            )}
          </motion.div>

          {chips.length > 0 && (
            <div className="flex gap-2 overflow-x-auto px-4 py-2 shrink-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {chips.map((chip, i) => (
                <motion.span
                  key={chip.key}
                  className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-medium ${chipColorClass(chip.type)}`}
                  {...(prefersReduced
                    ? {}
                    : {
                        initial: { opacity: 0, scale: 0.85 },
                        animate: { opacity: 1, scale: 1, transition: { delay: 0.12 + i * 0.04, duration: 0.2 } },
                      })}
                >
                  {chip.label}
                </motion.span>
              ))}
            </div>
          )}

          {(data.website || data.phone || data.instagramHandle) && (
            <div className="flex gap-2 overflow-x-auto px-4 pb-2 shrink-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {data.website && (
                <a
                  href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted text-xs text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors shrink-0"
                >
                  <Globe className="w-3 h-3" />
                  {(() => {
                    try {
                      return new URL(data.website.startsWith('http') ? data.website : `https://${data.website}`).hostname.replace(/^www\./, '')
                    } catch {
                      return 'Website'
                    }
                  })()}
                </a>
              )}
              {data.phone && (
                <a
                  href={`tel:${data.phone}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
                >
                  <Phone className="w-3 h-3" />
                  {data.phone}
                </a>
              )}
              {data.instagramHandle && (
                <a
                  href={`https://instagram.com/${data.instagramHandle.replace(/^@/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
                >
                  <Instagram className="w-3 h-3" />
                  @{data.instagramHandle.replace(/^@/, '')}
                </a>
              )}
            </div>
          )}

          <div className="px-4 py-2 flex flex-col gap-2 flex-1">
            {data.address && (
              <motion.div className="flex items-start gap-2 text-sm text-muted-foreground" {...stagger(1)}>
                <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
                <span>{[data.address, data.city].filter(Boolean).join(', ')}</span>
              </motion.div>
            )}

            {data.openUntil && (
              <motion.div className="flex items-center gap-2 text-sm" {...stagger(2)}>
                <Clock className="w-4 h-4 shrink-0 text-primary" />
                <span className="text-foreground">
                  <span className="text-emerald-500 font-medium">{t('preview.open') || 'Open'}</span>
                  {' '}{t('preview.until') || 'tot'} {data.openUntil}
                </span>
              </motion.div>
            )}

            {data.priceFrom != null && (
              <motion.div className="flex items-center gap-2 text-sm text-muted-foreground" {...stagger(3)}>
                <Euro className="w-4 h-4 shrink-0 text-primary" />
                <span>
                  {t('preview.from_price') || 'Vanaf'}{' '}
                  <span className="font-semibold text-foreground">&#8364;{data.priceFrom}</span>
                  {' / uur'}
                </span>
              </motion.div>
            )}

            {data.topReview && (
              <motion.div className="mt-1" {...stagger(4)}>
                <div className="h-px bg-border mb-2" />
                <p className="text-xs text-muted-foreground italic line-clamp-3">&ldquo;{data.topReview}&rdquo;</p>
              </motion.div>
            )}
          </div>

          {/* ── FOOTER ── */}
          <motion.div className="px-4 pt-3 pb-3 shrink-0 border-t border-border mt-auto" {...stagger(5)}>
            <button
              onClick={() => { onClose(); navigate(profileUrl) }}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl py-3.5 text-sm font-bold shadow-lg shadow-amber-500/20 hover:from-amber-400 hover:to-orange-400 transition-all focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              {t('preview.view_profile') || 'Bekijk volledig profiel'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          {data.profileType === 'unclaimed' && !data.website && !data.phone && (
            <p className="px-4 pb-1 text-xs text-muted-foreground/60 flex items-center gap-1.5">
              <span className={cn(
                "w-1.5 h-1.5 rounded-full bg-amber-400/60 inline-block",
                !prefersReduced && "animate-pulse"
              )} />
              Profiel nog niet volledig ingevuld
            </p>
          )}

          {data.profileType === 'unclaimed' && (
            <div className="mx-4 mb-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4">
              <p className="text-sm font-semibold text-foreground">
                {t('preview.claim_title') || 'Is dit jouw studio?'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('preview.claim_sub') || 'Claim dit profiel gratis en voeg je portfolio toe'}
              </p>
              <button
                onClick={() => { onClose(); navigate(`/studio/${data.slug}`) }}
                className="mt-3 w-full text-xs font-semibold text-amber-600 dark:text-amber-400 border border-amber-500/30 rounded-xl py-2 hover:bg-amber-500/10 transition-colors"
              >
                Claim gratis →
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </>
  )
}
